// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Global hover tooltip driven by [data-tip] attributes anywhere in the document.
// Canonical factory pattern (patterns/tooltip.ts) — copied, not re-rolled.
let tip: HTMLDivElement | null = null;

function ensure(): HTMLDivElement {
  if (!tip) {
    tip = document.createElement('div');
    tip.className = 'hover-tip';
    tip.setAttribute('role', 'tooltip');
    document.body.appendChild(tip);
  }
  return tip;
}

function position(el: HTMLDivElement, x: number, y: number): void {
  const pad = 12;
  const rect = el.getBoundingClientRect();
  let left = x + 14;
  let top = y + 14;
  if (left + rect.width + pad > window.innerWidth) left = x - rect.width - 14;
  if (top + rect.height + pad > window.innerHeight) top = y - rect.height - 14;
  el.style.left = `${Math.max(pad, left)}px`;
  el.style.top = `${Math.max(pad, top)}px`;
}

export function hideTip(): void {
  if (tip) tip.classList.remove('visible');
}

/** Show the tip anchored to an element rather than to the pointer. */
function showFor(target: Element): void {
  const text = target.getAttribute('data-tip') ?? '';
  if (!text) return;
  const el = ensure();
  el.textContent = text;
  el.classList.add('visible');
  const r = target.getBoundingClientRect();
  position(el, r.left + r.width / 2, r.bottom);
}

export function initTooltip(): void {
  let activeText = '';

  // Keyboard parity: every data mark that can be focused shows the same tip a
  // mouse user gets on hover, and Escape dismisses it. Without this the exact
  // values behind each mark are mouse-only.
  document.addEventListener('focusin', (e) => {
    const target = (e.target as Element).closest?.('[data-tip]');
    if (target) showFor(target);
  });
  document.addEventListener('focusout', () => hideTip());
  document.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Escape') hideTip();
  });
  document.addEventListener('mouseover', (e) => {
    const target = (e.target as Element).closest?.('[data-tip]');
    if (!target) return;
    const text = target.getAttribute('data-tip') ?? '';
    if (!text) return;
    activeText = text;
    const el = ensure();
    // Multi-line tips arrive with real newlines; render them as lines.
    el.textContent = text;
    el.classList.add('visible');
    position(el, (e as MouseEvent).clientX, (e as MouseEvent).clientY);
  });
  document.addEventListener('mousemove', (e) => {
    if (!tip || !tip.classList.contains('visible')) return;
    const target = (e.target as Element).closest?.('[data-tip]');
    if (!target || target.getAttribute('data-tip') !== activeText) {
      tip.classList.remove('visible');
      return;
    }
    position(tip, (e as MouseEvent).clientX, (e as MouseEvent).clientY);
  });
  document.addEventListener('mouseout', (e) => {
    const target = (e.target as Element).closest?.('[data-tip]');
    if (target && tip) tip.classList.remove('visible');
  });
  window.addEventListener('scroll', hideTip, true);
}
